import { useEffect, useState } from "react";
import { firstValueFrom, Subject } from "rxjs";
import Button from "@mui/material/Button";
import "./AlertBar.css"; // Because I don't want to figure out how to translate the css animation into tailwind
import MyconBtn from "./MyconButton";
import { TextField } from "@mui/material";
import { onSubmit } from "../ReactAliases";

type Color = keyof typeof COLOR_LU;

interface BtnFig {
  text: string;
  color: Parameters<typeof Button>[0]["color"]; // the MUI color type
}

export interface Alert {
  msg: string;
  color: Color;
  done?: number | BtnFig[] | "SF_PW_PROMPT"; // either a timeout or it's a prompt with buttons // I could make buttons optionally inputs, but that's a whole thing and I'd rather do it manually, like where else am I going to prompt with a text input?
}

type Msg = (msg: string, timeout?: number) => void;

const $ = new Subject<Alert>();
const prompResponse$ = new Subject<string>(); // For when I give the user a prompt, this is where the response gets emitted

export const say: Msg = (msg, done) => $.next({ msg, color: "grey", done });
export const celebrate: Msg = (msg, done) =>
  $.next({ msg, color: "green", done });
export const warn: Msg = (msg, done) => $.next({ msg, color: "gold", done });
export const error: Msg = (msg, done) => $.next({ msg, color: "red", done });

export const areYouSure = (msg: string, color: Color = "gold") => {
  const done: BtnFig[] = [
    { text: "Yes", color: "secondary" },
    { text: "No", color: "primary" },
  ];
  $.next({ msg, color, done });
  return firstValueFrom(prompResponse$).then((val) => {
    if (val === "No") return Promise.reject("You said no");
    return val;
  });
};

export const ask = (
  msg: string,
  done: BtnFig[] | "SF_PW_PROMPT",
  color: Color = "gold"
) => {
  $.next({ msg, color, done });
  return firstValueFrom(prompResponse$);
};

const nextAlert = new Subject<void>();
// It was not working right to have these defined on the component
var pending: Alert[] = [];
var nextTimeout: any;
var slideDownTimeout: any;

export default function AlertBar() {
  const [toShow, setToShow] = useState<Alert | undefined>();
  const [showing, setShowing] = useState(false);

  function isPrompt(a: Alert) {
    return !(typeof (a.done || 10) === "number");
  }

  useEffect(() => {
    const sub1 = $.subscribe((alert) => {
      pending.push(alert);
      if (!showing) nextAlert.next();
    });

    const sub2 = nextAlert.subscribe(() => {
      const next = pending[0]; // Will be undefined when I've run through my pending's.

      clearTimeout(nextTimeout); // Clear any leftover timeout, there should only ever be one running and it should be cleared with the x button
      clearTimeout(slideDownTimeout);
      if (!next) {
        setShowing(false);
        slideDownTimeout = setTimeout(() => setToShow(undefined), 500); // if I'm swapping to nothing, I want to preserve the contents for the duration of the animation (500ms)
      } else {
        setToShow(next);
        pending = pending.slice(1);
        if (!isPrompt(next))
          nextTimeout = setTimeout(() => {
            nextAlert.next();
          }, (next?.done as any) || 3500);
        // otherwise, no timeout and it has to be triggered by the button click
        setTimeout(() => setShowing(true), 0); // set showing in the next event loop so I get the animation of it popping in
      }
    });

    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
    };
  }, [showing]);

  function respond(val: string) {
    nextAlert.next();
    prompResponse$.next(val);
  }

  return !toShow ? (
    <span></span>
  ) : (
    <div
      className={`snackbar z-50 top-full left-1/2 fixed flex flex-col justify-center items-center ${
        showing ? "showing" : ""
      }`}>
      <div
        className={`px-4 shadow rounded-xl border ${COLOR_LU[toShow.color]}`}>
        {toShow.msg.split("\n").map((p, i) => (
          <div key={i} className="my-2">
            {p}
          </div>
        ))}
        {!isPrompt(toShow) && (
          <MyconBtn
            onClick={() => (showing ? nextAlert.next() : "")} // The x is still visible for the animation (which starts when showing is toggled) and I don't want it to do the thing if it's already going away
            className="!absolute !-right-12 !top-0.5 !border-grey-500 !bg-grey-200 border">
            close
          </MyconBtn>
        )}
      </div>
      {isPrompt(toShow) && (
        <div className="flex gap-8 mt-3">
          {toShow.done !== "SF_PW_PROMPT" ? (
            (toShow.done as BtnFig[]).map((btn, i) => (
              <Button
                onClick={() => respond(btn.text)}
                key={i}
                variant="contained"
                color={btn.color}>
                {btn.text}
              </Button>
            ))
          ) : (
            <div className="bg-grey-300 rounded p-2">
              <TextField
                label="New Salesforce Password"
                onKeyDown={(e) =>
                  e.key === "Enter" ? respond((e.target as any)?.value) : ""
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const COLOR_LU = {
  gold: "bg-gold-200 border-gold-500",
  green: "bg-green-200 border-green-500",
  grey: "bg-grey-200 border-grey-500",
  red: "bg-red-200 border-red-500",
};

// Couple of buttons for testing if I decide to mess with this again

export function TestAlertButtons() {
  return (
    <>
      <Button
        variant="contained"
        color="info"
        onClick={() => say("Hello there")}>
        Say
      </Button>
      <Button
        variant="contained"
        color="success"
        onClick={() => celebrate("The angel from my nightmare")}>
        Celebrate
      </Button>
      <Button
        variant="contained"
        color="warning"
        onClick={() => warn("The shadow in the background of the morgue")}>
        Warn!
      </Button>
      <Button
        variant="contained"
        color="error"
        onClick={() => error("The unsuspecting vitim")}>
        Error
      </Button>
      <Button
        variant="contained"
        color="secondary"
        onClick={() =>
          ask("And I'm so sorry", [
            { text: "I cannot sleep", color: "secondary" },
            { text: "I cannot sleep", color: "primary" },
          ]).then((val) => {
            console.log(val);
          })
        }>
        Where are you
      </Button>
    </>
  );
}
